import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { ProjectSkeleton } from '../components/SkeletonLoader';
import CreativesGallery from '../components/CreativesGallery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FadeIn = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.23, 1, 0.32, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${id}`);
        setProject(response.data);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle Google Drive (file/d/ID/ or open?id=ID)
    const driveMatch = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/);
    if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
    // Handle YouTube Shorts
    const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
    if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
    // Handle regular YouTube
    const videoId = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]+)/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto"><ProjectSkeleton /></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg" style={{ color: 'var(--text-tertiary)' }}>Project not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6" data-testid="project-detail-page">
      <div className="max-w-5xl mx-auto">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm transition-opacity hover:opacity-60"
            style={{ color: 'var(--text-secondary)', fontFamily: 'Nohemi, sans-serif' }}
            data-cursor-hover
            data-testid="back-to-work"
          >
            <ArrowLeft size={16} />
            Back to Work
          </Link>
        </motion.div>

        {/* Hero Image */}
        <FadeIn>
          <div className="aspect-[16/9] overflow-hidden mb-12" style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <img
              src={project.image_url || 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=1200'}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
        </FadeIn>

        {/* Project Info */}
        <div className="space-y-12">
          <FadeIn delay={0.1}>
            <p className="text-sm uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)', fontFamily: 'Nohemi, sans-serif' }}>
              {project.client}
            </p>
            <h1
              className="font-bold mb-6"
              style={{ fontSize: 'clamp(3rem, 6vw, 6rem)', lineHeight: '1.1', letterSpacing: '-0.02em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              {project.title}
            </h1>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {project.description}
            </p>
          </FadeIn>

          {/* Videos */}
          {project.video_urls && project.video_urls.length > 0 && (
            <FadeIn delay={0.2}>
              <h2
                className="font-bold mb-8"
                style={{ fontSize: 'clamp(2rem, 4vw, 4rem)', lineHeight: '1.2', letterSpacing: '-0.02em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
              >
                {project.video_section_title || 'Campaign Films'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {project.video_urls.map((videoUrl, index) => {
                  const embedUrl = getYouTubeEmbedUrl(videoUrl);
                  return embedUrl ? (
                    <div key={videoUrl} className="aspect-video">
                      <iframe
                        src={embedUrl}
                        title={`${project.title} - Video ${index + 1}`}
                        frameBorder="0"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                  ) : null;
                })}
              </div>
            </FadeIn>
          )}

          {/* Creative Gallery */}
          {project.creatives && project.creatives.length > 0 && (
            <FadeIn delay={0.25}>
              <div className="flex items-baseline justify-between mb-10">
                <h2
                  className="font-bold"
                  style={{ fontSize: 'clamp(2rem, 4vw, 4rem)', lineHeight: '1.2', letterSpacing: '-0.02em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
                >
                  Campaign Creatives
                </h2>
                <p
                  className="hidden sm:block text-xs uppercase tracking-[0.3em]"
                  style={{ color: 'var(--text-tertiary)', fontFamily: 'Nohemi, sans-serif' }}
                >
                  {String(project.creatives.length).padStart(2, '0')} pieces
                </p>
              </div>
              <CreativesGallery creatives={project.creatives} />
            </FadeIn>
          )}

          {/* Metrics */}
          {project.metrics && Object.keys(project.metrics).length > 0 && (
            <FadeIn delay={0.3}>
              <div className="py-12" style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
                <h2
                  className="font-bold mb-8"
                  style={{ fontSize: 'clamp(2rem, 4vw, 4rem)', lineHeight: '1.2', letterSpacing: '-0.02em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
                >
                  Results
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm uppercase tracking-wider mb-2" style={{ color: 'var(--text-tertiary)', fontFamily: 'Nohemi, sans-serif' }}>
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}

          {project.campaign_link && (
            <FadeIn delay={0.4}>
              <a
                href={project.campaign_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-base underline hover:opacity-60 transition-opacity"
                style={{ color: 'var(--text-primary)' }}
                data-cursor-hover
              >
                View Campaign Website
              </a>
            </FadeIn>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
